'use client'

import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import AddressManager, {
    EditableAddress,
} from '@/components/address/address-manager'
import { getAddresses, syncAddresses, toEditable } from '@/lib/api/addresses'
import {
    createHospital,
    getHospital,
    updateHospital,
} from '@/lib/api/hospitals/hospital-api'
import { useAuth } from '@/store/hooks'
import { mask, unMask } from '@/lib/form-state/util'
import {
    PayoutScheduleSection,
    PayoutScheduleValue,
    payoutScheduleToPayload,
    validatePayoutScheduleClient,
} from '@/components/payout-schedule/PayoutScheduleSection'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdBusiness,
    MdLocalHospital,
    MdLocationOn,
    MdNotes,
    MdSave,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface HospitalFormData {
    name: string
    tradingName: string
    cnpj: string
    cnes: string
    phone: string
    phone2: string
    email: string
    website: string
    street: string
    number: string
    complement: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
    description: string
    active: boolean
}

const INITIAL_FORM: HospitalFormData = {
    name: '',
    tradingName: '',
    cnpj: '',
    cnes: '',
    phone: '',
    phone2: '',
    email: '',
    website: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    description: '',
    active: true,
}

const TABS = [
    { key: 'basic', label: 'Informações Básicas', icon: MdBusiness },
    { key: 'address', label: 'Endereço', icon: MdLocationOn },
    { key: 'description', label: 'Descrição', icon: MdNotes },
] as const

type TabKey = (typeof TABS)[number]['key']

const formatCnpj = (value: string): string => {
    const digits = unMask(value)
    return mask(digits, '99.999.999/9999-99')
}

const formatCep = (value: string): string => {
    const digits = unMask(value)
    return mask(digits, '99999-999')
}

export default function HospitalFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEdit = !!id

    const [form, setForm] = useState<HospitalFormData>(INITIAL_FORM)
    const [payoutSchedule, setPayoutSchedule] = useState<PayoutScheduleValue>({
        payoutScheduleType: 'OPEN',
        payoutFixedDay: '',
        payoutDaysAfterAppointment: '',
    })
    const [loading, setLoading] = useState(false)
    /** Unidades do hospital. Persistidas no submit, via `syncAddresses`. */
    const [addresses, setAddresses] = useState<EditableAddress[]>([])
    const [saving, setSaving] = useState(false)
    const [activeTab, setActiveTab] = useState<TabKey>('basic')
    const [loadingCep, setLoadingCep] = useState(false)

    useEffect(() => {
        if (isEdit && id) {
            loadHospital(parseInt(id, 10))
        }
    }, [id])

    const loadHospital = async (hospitalId: number) => {
        setLoading(true)
        try {
            const response = await getHospital(hospitalId)
            if (response.success && response.result) {
                const h = response.result
                setForm({
                    name: h.name || '',
                    tradingName: h.tradingName || '',
                    cnpj: h.cnpj ? formatCnpj(h.cnpj) : '',
                    cnes: h.cnes || '',
                    phone: h.phone || '',
                    phone2: h.phone2 || '',
                    email: h.email || '',
                    website: h.website || '',
                    street: h.street || '',
                    number: h.number || '',
                    complement: h.complement || '',
                    neighborhood: h.neighborhood || '',
                    city: h.city || '',
                    state: h.state || '',
                    zipCode: h.zipCode ? formatCep(h.zipCode) : '',
                    description: h.description || '',
                    active: h.active,
                })
                const addressResponse = await getAddresses({ hospitalId })
                if (addressResponse.success) {
                    setAddresses(toEditable(addressResponse.result))
                }
                setPayoutSchedule({
                    payoutScheduleType: h.payoutScheduleType || 'OPEN',
                    payoutFixedDay: h.payoutFixedDay
                        ? String(h.payoutFixedDay)
                        : '',
                    payoutDaysAfterAppointment: h.payoutDaysAfterAppointment
                        ? String(h.payoutDaysAfterAppointment)
                        : '',
                })
            } else {
                toast.error('Hospital não encontrado')
                navigate('/registrations/hospitals')
            }
        } catch {
            toast.error('Erro ao carregar hospital')
            navigate('/registrations/hospitals')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (
        field: keyof HospitalFormData,
        value: string | boolean,
    ) => {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const handleZipCodeChange = async (value: string) => {
        const formatted = formatCep(value)
        handleChange('zipCode', formatted)

        const cleanZip = unMask(value)
        if (cleanZip.length === 8) {
            setLoadingCep(true)
            try {
                const data = await fetchPostalCode(cleanZip)
                if (data && !data.erro) {
                    setForm(prev => ({
                        ...prev,
                        street: data.logradouro || prev.street,
                        neighborhood: data.bairro || prev.neighborhood,
                        city: data.localidade || prev.city,
                        state: data.uf || prev.state,
                        complement: data.complemento || prev.complement,
                    }))
                }
            } catch {
                // CEP não encontrado
            } finally {
                setLoadingCep(false)
            }
        }
    }

    const validate = (): boolean => {
        if (!form.name.trim()) {
            toast.error('Nome do hospital é obrigatório')
            setActiveTab('basic')
            return false
        }
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error('Email inválido')
            setActiveTab('basic')
            return false
        }
        const payoutError = validatePayoutScheduleClient(payoutSchedule)
        if (payoutError) {
            toast.warning(payoutError)
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) return
        if (!user?.organizationId) return

        setSaving(true)
        try {
            const data: any = {
                name: form.name.trim(),
                active: form.active,
                organizationId: user.organizationId,
            }

            if (form.tradingName.trim()) data.tradingName = form.tradingName.trim()
            if (form.cnpj.trim()) data.cnpj = form.cnpj.trim()
            if (form.cnes.trim()) data.cnes = form.cnes.trim()
            if (form.phone.trim()) data.phone = form.phone.trim()
            if (form.phone2.trim()) data.phone2 = form.phone2.trim()
            if (form.email.trim()) data.email = form.email.trim()
            if (form.website.trim()) data.website = form.website.trim()
            // Endereço não vai mais no payload do hospital: virou tabela própria,
            // gravada por `syncAddresses` logo abaixo. Ver model `Address`.
            if (form.description.trim()) data.description = form.description.trim()

            Object.assign(data, payoutScheduleToPayload(payoutSchedule))

            let response
            if (isEdit && id) {
                response = await updateHospital(parseInt(id, 10), data)
            } else {
                response = await createHospital(data)
            }

            if (response.success) {
                // Só dá para gravar endereço depois que o hospital existe: na
                // criação o id nasce aqui.
                const hospitalId = isEdit
                    ? parseInt(id!, 10)
                    : response.result?.id
                if (hospitalId) {
                    try {
                        await syncAddresses({ hospitalId }, addresses)
                    } catch {
                        toast.error(
                            'Hospital salvo, mas houve erro ao salvar os endereços',
                        )
                    }
                }

                toast.success(
                    isEdit
                        ? 'Hospital atualizado com sucesso'
                        : 'Hospital criado com sucesso',
                )
                navigate('/registrations/hospitals')
            } else {
                toast.error(
                    response.error ||
                    (isEdit
                        ? 'Erro ao atualizar hospital'
                        : 'Erro ao criar hospital'),
                )
            }
        } catch {
            toast.error(
                isEdit
                    ? 'Erro ao atualizar hospital'
                    : 'Erro ao criar hospital',
            )
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando hospital...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/registrations/hospitals')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdLocalHospital className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEdit ? 'Editar Hospital' : 'Novo Hospital'}
                        </h1>
                        <p className="text-sm text-base-content/60 mt-1">
                            {isEdit
                                ? 'Atualize os dados do hospital'
                                : 'Preencha os dados do novo hospital'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div
                role="tablist"
                className="tabs tabs-bordered tabs-lg overflow-x-auto"
            >
                {TABS.map(tab => {
                    const TabIcon = tab.icon
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            className={`tab whitespace-nowrap !rounded-none gap-2 ${activeTab === tab.key ? 'tab-active border-b-2 border-b-primary' : ''
                                }`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <TabIcon className="text-base" />
                            {tab.label}
                        </button>
                    )
                })}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                    {/* Tab: Informações Básicas */}
                    {activeTab === 'basic' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome do Hospital *
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome do hospital"
                                    className="input input-bordered w-full"
                                    value={form.name}
                                    onChange={e =>
                                        handleChange('name', e.target.value)
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome Fantasia
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome fantasia"
                                    className="input input-bordered w-full"
                                    value={form.tradingName}
                                    onChange={e =>
                                        handleChange(
                                            'tradingName',
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        CNPJ
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="00.000.000/0000-00"
                                    className="input input-bordered w-full"
                                    maxLength={18}
                                    value={form.cnpj}
                                    onChange={e =>
                                        handleChange('cnpj', formatCnpj(e.target.value))
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        CNES
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cadastro Nacional de Estabelecimentos de Saúde"
                                    className="input input-bordered w-full"
                                    value={form.cnes}
                                    onChange={e =>
                                        handleChange('cnes', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Email
                                    </span>
                                </label>
                                <input
                                    type="email"
                                    placeholder="email@hospital.com"
                                    className="input input-bordered w-full"
                                    value={form.email}
                                    onChange={e =>
                                        handleChange('email', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Telefone
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="(00) 0000-0000"
                                    className="input input-bordered w-full"
                                    value={form.phone}
                                    onChange={e =>
                                        handleChange('phone', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Telefone Secundário
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="(00) 0000-0000"
                                    className="input input-bordered w-full"
                                    value={form.phone2}
                                    onChange={e =>
                                        handleChange('phone2', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Website
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="https://www.hospital.com"
                                    className="input input-bordered w-full"
                                    value={form.website}
                                    onChange={e =>
                                        handleChange('website', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={form.active}
                                        onChange={e =>
                                            handleChange(
                                                'active',
                                                e.target.checked,
                                            )
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Hospital Ativo
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Tab: Enderecos (multiplas unidades) */}
                    {activeTab === 'address' && (
                        <div className="flex flex-col gap-4">
                            <p className="text-sm text-base-content/60">
                                Um hospital pode ter mais de uma unidade ou bloco.
                                Dê um título a cada endereço (ex.: &quot;Unidade
                                Centro&quot;) para que o operador saiba qual
                                escolher no agendamento.
                            </p>
                            <AddressManager
                                value={addresses}
                                onChange={setAddresses}
                                disabled={loading}
                                emptyHint="Nenhum endereço cadastrado para este hospital."
                            />
                        </div>
                    )}

                    {/* Tab: Descrição */}
                    {activeTab === 'description' && (
                        <div className="space-y-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Descrição
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full min-h-[200px]"
                                    placeholder="Descrição do hospital, especialidades, informações adicionais..."
                                    value={form.description}
                                    onChange={e =>
                                        handleChange('description', e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6">
                    <PayoutScheduleSection
                        value={payoutSchedule}
                        onChange={setPayoutSchedule}
                        description="Defina como os valores serão repassados a este hospital."
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/registrations/hospitals')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <span className="loading loading-spinner loading-sm" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                {isEdit ? 'Salvar Alterações' : 'Criar Hospital'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
